import BannerComponent, { Props as BannerProps } from '../banner/banner.component';
import CarouselComponent, { CarouselType } from '../carousel/carousel.component';
import { ReactElement, memo } from 'react';
import { CmsComponentContext } from '../container/CmsComponentContext';
import ConfigurableCmsComponentMetadata from '../../top-level-components/cms-page/metadata/configurable/configurable-cms-component-metadata.component';
import ConfigurableCmsSlotMetadata from '../../top-level-components/cms-page/metadata/configurable/configurable-cms-slot-metadata.component';
import ConfigurableCmsValidity from '@spa/top-level-components/cms-page/metadata/configurable/configurable-cms-validity.component';
import HeadlineComponent from '../headline/headline.component';
import { LinkComponentLink } from '../link/link.component';
import { Metadata } from '@spa/pages-converters/cms/component-envelops/CmsComponentEnvelopBase';
import styles from './banner-slider.component.module.scss';

interface BannerMetadata {
  readonly metadata: ReadonlyArray<Metadata>;
}

export type Banner = BannerProps & BannerMetadata;

export interface Props {
  readonly id: string;
  readonly banners: ReadonlyArray<Banner>;
  readonly bannersMetadata: ReadonlyArray<Metadata>;
  readonly dateFrom: number | null;
  readonly dateTo: number | null;
  readonly editorMode: boolean;
  readonly headline: string | null;
  readonly headlineLink: LinkComponentLink | null;
  readonly subHeadline: string | null;
  readonly priority: boolean;
}

function BannerSliderComponent({
  id,
  banners,
  bannersMetadata,
  dateFrom,
  dateTo,
  editorMode,
  headline,
  subHeadline,
  headlineLink,
  priority
}: Props): ReactElement {
  return (
    <>
      {editorMode && <ConfigurableCmsValidity dateFrom={dateFrom} dateTo={dateTo} />}
      {editorMode && <ConfigurableCmsSlotMetadata beforeSlot={true} metadata={bannersMetadata} />}
      <div className={styles['banner-slider']}>
        {headline && (
          <HeadlineComponent
            id={`${id}-headline`}
            headline={headline}
            link={headlineLink}
            subHeadline={subHeadline}
            headlineTag={'h2'}
          />
        )}
        <CarouselComponent
          id={`${id}-carousel`}
          carouselType={CarouselType.BANNER}
          navigation={false}
          autoPlay={false}
        >
          {banners.map(
            (
              { images, cta, enableNextJsLoader, metadata, dateTo, dateFrom }: Banner,
              bannerIdx
            ) => (
              <div key={`${images.imageDesktop.id + bannerIdx}`}>
                {editorMode && (
                  <ConfigurableCmsComponentMetadata beforeComponent={true} metadata={metadata} />
                )}
                <BannerComponent
                  id={`${id}-banner-${bannerIdx}`}
                  context={CmsComponentContext.DEFAULT}
                  images={images}
                  cta={cta}
                  priority={priority && bannerIdx === 0}
                  enableNextJsLoader={enableNextJsLoader}
                  headline={null}
                  subHeadline={null}
                  headlineLink={null}
                  dateTo={dateTo}
                  dateFrom={dateFrom}
                  editorMode={editorMode}
                />
                {editorMode && (
                  <ConfigurableCmsComponentMetadata beforeComponent={false} metadata={metadata} />
                )}
              </div>
            )
          )}
        </CarouselComponent>
      </div>
      {editorMode && <ConfigurableCmsSlotMetadata beforeSlot={false} metadata={bannersMetadata} />}
    </>
  );
}

export default memo(BannerSliderComponent);
